import { useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { restClient } from '@/api/client'
import { endpoints } from '@/api/endpoints'
import { useAuthStore } from '@/store/useAuthStore'
import { useDebounce } from '@/shared/hooks'
import { Search, X, Mail, Phone, Building2, Calendar, ChevronLeft, ChevronRight } from 'lucide-react'
import { cn } from '@/shared/utils'

// ─── Single request card ───────────────────────────────────────────────────────
function RequestCard({ request, onReload, isSchoolAdmin }: { request: any; onReload: () => void; isSchoolAdmin: boolean }) {
  const { t }      = useTranslation()
  const navigate   = useNavigate()
  const [loading,  setLoading]  = useState<'accept'|'reject'|null>(null)
  const [toast,    setToast]    = useState<{msg:string;type:'ok'|'err'}|null>(null)

  const showToast = (msg: string, type: 'ok'|'err') => {
    setToast({msg,type})
    setTimeout(()=>setToast(null), 3000)
  }

  const handleAction = useCallback(async (verify: 0 | 1) => {
    const action = verify === 1 ? 'accept' : 'reject'
    setLoading(action)
    try {
      let res: any
      if (isSchoolAdmin) {
        res = await restClient.post(endpoints.pendingRequest.updateVerification, {
          trainer_id: request?.user_id,
          verify,
        })
      } else {
        res = await restClient.post(endpoints.pendingRequest.rejectAccept, {
          mapping_id: request?.id,
          verify,
        })
      }
      showToast(res.data?.message ?? t('trainerVerification.done'), 'ok')
      onReload()
    } catch(e: any) {
      const errs = e?.response?.data?.errors
      const msg  = errs ? Object.values(errs).flat().join(', ') : (e?.response?.data?.message ?? t('trainerVerification.failed'))
      showToast(msg, 'err')
    } finally { setLoading(null) }
  }, [request, isSchoolAdmin, onReload])

  // Trainer info
  const trainer     = request?.user ?? {}
  const photoUrl    = trainer?.photo_url
  const name        = trainer?.name ?? '—'
  const email       = trainer?.email ?? '—'
  const phone       = trainer?.phone ?? '—'
  const countryCode = trainer?.country_code ?? '971'

  // School name (En locale preferred) — API uses vendor.translations (or vendor_translations)
  const vendorTr    = request?.vendor?.translations ?? request?.vendor?.vendor_translations
  const schoolName  = vendorTr?.find(
    (tr: any) => tr?.locale?.toLowerCase() === 'en'
  )?.name ?? vendorTr?.[0]?.name ?? request?.school_name ?? '—'
  const vendorId    = request?.vendor?.id ?? vendorTr?.[0]?.vendor_id

  // City
  const cityName = trainer?.user_preference?.city?.city_translations?.find(
    (tr: any) => tr?.locale?.toLowerCase() === 'en'
  )?.name ?? trainer?.user_preference?.city?.city_translations?.[0]?.name ?? ''

  // Vehicle type
  const vehicleName = trainer?.user_preference?.vehicle_type?.category_translations?.find(
    (tr: any) => tr?.locale?.toLowerCase() === 'en'
  )?.name ?? trainer?.user_preference?.vehicle_type?.category_translations?.[0]?.name ?? ''

  // Requested time — falls back to created_at when requested_time is absent
  const reqTimeRaw = request?.requested_time ?? request?.created_at
  const reqTime = reqTimeRaw
    ? new Date(reqTimeRaw).toLocaleString('en-GB',{day:'2-digit',month:'2-digit',year:'numeric',hour:'2-digit',minute:'2-digit'}).replace(',','')
    : '—'

  return (
    <>
      {toast && (
        <div className={cn('fixed top-4 end-4 z-[100] px-4 py-3 rounded-xl shadow-lg text-sm font-semibold text-white transition-all',
          toast.type==='ok' ? 'bg-green-500' : 'bg-red-500')}>
          {toast.msg}
        </div>
      )}

      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden hover:shadow-md transition-shadow">
        {/* Top: avatar + info */}
        <div className="p-4">
          <div className="flex items-start gap-3">
            {/* Avatar */}
            <div className="flex-shrink-0">
              {photoUrl ? (
                <img src={photoUrl} alt={name}
                  className="w-12 h-12 rounded-full object-cover border-2 border-gray-100"/>
              ) : (
                <div className="w-12 h-12 rounded-full bg-gray-200 flex items-center justify-center text-gray-500 font-bold text-lg border-2 border-gray-100">
                  {name[0]?.toUpperCase() ?? 'T'}
                </div>
              )}
            </div>

            {/* Info */}
            <div className="flex-1 min-w-0 space-y-1.5">
              {/* Name */}
              <button onClick={() => trainer?.id && navigate(isSchoolAdmin ? `/school/trainer/${trainer.id}` : `/dashboard/user/${trainer.id}`)}
                className="text-sm font-bold text-gray-900 hover:underline text-start truncate block w-full">
                {name}
              </button>

              {/* School */}
              <button onClick={() => isSchoolAdmin ? navigate('/school/account') : (vendorId && navigate(`/dashboard/school/${vendorId}`))}
                className="flex items-center gap-1.5 text-xs text-brand-600 hover:underline text-start">
                <Building2 size={11} className="flex-shrink-0"/>
                <span className="truncate">{schoolName}</span>
              </button>

              {/* Email + Phone */}
              <div className="flex items-center flex-wrap gap-x-4 gap-y-1">
                <span className="flex items-center gap-1 text-xs text-gray-500">
                  <Mail size={11} className="flex-shrink-0"/>
                  <span className="truncate max-w-[160px]">{email}</span>
                </span>
                <span className="flex items-center gap-1 text-xs text-gray-500">
                  <Phone size={11} className="flex-shrink-0"/>
                  +{countryCode} {phone}
                </span>
              </div>

              {/* Date */}
              <div className="flex items-center gap-1 text-xs text-gray-400">
                <Calendar size={11} className="flex-shrink-0"/>
                {reqTime}
              </div>

              {/* City + Vehicle */}
              {(cityName || vehicleName) && (
                <div className="flex items-center gap-2 flex-wrap">
                  {cityName   && <span className="text-xs text-gray-400 bg-gray-50 rounded-full px-2 py-0.5">{cityName}</span>}
                  {vehicleName && <span className="text-xs text-gray-400 bg-gray-50 rounded-full px-2 py-0.5">{vehicleName}</span>}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Action buttons */}
        <div className="grid grid-cols-2 gap-0 border-t border-gray-100">
          <button
            onClick={() => handleAction(0)}
            disabled={!!loading}
            className="py-2.5 text-sm font-bold text-red-500 bg-red-50 hover:bg-red-100 disabled:opacity-50 transition-colors border-e border-gray-100">
            {loading === 'reject' ? '...' : t('trainerVerification.reject')}
          </button>
          <button
            onClick={() => handleAction(1)}
            disabled={!!loading}
            className="py-2.5 text-sm font-bold text-green-600 bg-green-50 hover:bg-green-100 disabled:opacity-50 transition-colors">
            {loading === 'accept' ? '...' : t('trainerVerification.accept')}
          </button>
        </div>
      </div>
    </>
  )
}

// ─── MAIN PAGE ────────────────────────────────────────────────────────────────
export function TrainerPendingVerificationPage() {
  const { t }       = useTranslation()
  const { user }    = useAuthStore()
  const userType    = (user as any)?.user_type ?? (user as any)?.user?.user_type
  const isSchoolAdmin = String(userType ?? '').toUpperCase() === 'SCHOOL_ADMIN'

  const [search,  setSearch]  = useState('')
  const [page,    setPage]    = useState(1)
  const [perPage, setPerPage] = useState(20)
  const debouncedSearch = useDebounce(search)
  const qc = useQueryClient()

  const listEndpoint = isSchoolAdmin
    ? endpoints.pendingRequest.schoolAdminList
    : endpoints.pendingRequest.trainerPendingRequest

  const { data, isLoading, isError } = useQuery({
    queryKey: ['pendingVerification', page, perPage, debouncedSearch, isSchoolAdmin],
    queryFn: () => {
      const p = new URLSearchParams({
        page:        String(page),
        limit:       String(perPage),
        sort_by:     'desc',
        is_verified: '0',
      })
      if (debouncedSearch) p.append('search', debouncedSearch)
      return restClient.get(`${listEndpoint}?${p.toString()}`).then(r => r.data)
    },
  })

  const rows:  any[]  = (data as any)?.data  ?? []
  const total: number = (data as any)?.total ?? 0
  const lastPage = Math.ceil(total / perPage) || 1

  const reload = useCallback(() => {
    qc.invalidateQueries({ queryKey: ['pendingVerification'] })
  }, [qc])

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2 text-xs text-gray-400 mb-1">
            <span>{t('trainerVerification.breadcrumb.dashboard')}</span><span>/</span><span>{t('trainerVerification.breadcrumb.todo')}</span><span>/</span>
            <span className="text-brand-500 font-medium">{t('trainerVerification.breadcrumb.pendingVerification')}</span>
          </div>
          <h1 className="text-xl font-bold text-gray-900">{t('trainerVerification.title')}</h1>
        </div>

        {/* Search */}
        <div className="flex items-center gap-2 border border-gray-200 rounded-lg px-3 py-2 bg-white min-w-72">
          <Search size={13} className="text-gray-400 flex-shrink-0"/>
          <input
            value={search}
            onChange={e => { setSearch(e.target.value); setPage(1) }}
            placeholder={t('trainerVerification.searchPlaceholder')}
            className="text-xs text-gray-600 placeholder-gray-400 focus:outline-none flex-1 bg-transparent"
          />
          {search && (
            <button onClick={() => { setSearch(''); setPage(1) }}>
              <X size={11} className="text-gray-300 hover:text-gray-500"/>
            </button>
          )}
        </div>
      </div>

      {/* Cards grid */}
      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {Array.from({length: 6}).map((_,i) => (
            <div key={i} className="h-52 rounded-2xl bg-gray-100 animate-pulse"/>
          ))}
        </div>
      ) : isError ? (
        <div className="bg-white rounded-2xl border border-red-100 p-10 text-center">
          <p className="text-red-500 text-sm">{t('trainerVerification.loadFailed')}</p>
        </div>
      ) : rows.length === 0 ? (
        <div className="bg-white rounded-2xl border border-gray-100 p-16 text-center">
          <p className="text-gray-400 text-sm">{t('trainerVerification.empty')}</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {rows.map((req: any) => (
            <RequestCard key={req.id} request={req} onReload={reload} isSchoolAdmin={isSchoolAdmin}/>
          ))}
        </div>
      )}

      {/* Pagination */}
      {total > 0 && (
        <div className="flex items-center justify-between bg-white rounded-xl border border-gray-100 px-5 py-3 flex-wrap gap-3">
          <div className="flex items-center gap-3 text-sm text-gray-500">
            <span>{t('trainerVerification.itemsPerPage')}</span>
            <select value={perPage} onChange={e => { setPerPage(Number(e.target.value)); setPage(1) }}
              className="border border-gray-200 rounded-lg px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-brand-400">
              {[10,15,20,25,50].map(n => <option key={n} value={n}>{n}</option>)}
            </select>
            <span>{t('trainerVerification.itemsRange', { start: ((page-1)*perPage)+1, end: Math.min(page*perPage,total), total: total.toLocaleString() })}</span>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => setPage(p=>p-1)} disabled={page<=1}
              className="flex items-center gap-1 px-3 py-1.5 text-sm border border-gray-200 rounded-lg disabled:opacity-40 hover:bg-gray-50 text-gray-600">
              <ChevronLeft size={14} className="rtl:rotate-180"/> {t('common.pagination.previous')}
            </button>
            {Array.from({length: Math.min(5,lastPage)}, (_,i) => {
              const pg = Math.max(1, Math.min(page-2, lastPage-4)) + i
              return (
                <button key={pg} onClick={() => setPage(pg)}
                  className={cn('w-8 h-8 text-sm rounded-lg border font-medium',
                    pg===page ? 'bg-brand-500 text-white border-brand-500' : 'border-gray-200 hover:bg-gray-50 text-gray-600')}>
                  {pg}
                </button>
              )
            })}
            <span className="text-sm text-gray-400">{t('common.pagination.of')} {lastPage}</span>
            <button onClick={() => setPage(p=>p+1)} disabled={page>=lastPage}
              className="flex items-center gap-1 px-3 py-1.5 text-sm border border-gray-200 rounded-lg disabled:opacity-40 hover:bg-gray-50 text-gray-600">
              {t('common.pagination.next')} <ChevronRight size={14} className="rtl:rotate-180"/>
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
